import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import ReactSelect from "react-select";
import swal from "sweetalert";
import {
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  ImageIcon,
  LayoutGrid,
  Lock,
  List as ListIcon,
  Search,
  Sparkles,
  Tag,
  Trash2,
  Upload,
} from "lucide-react";

import Spinner from "../../../Layouts/Spinner";
import FormField from "../../../Common/FormField/FormField";
import { handleImageUpload } from "../../../Common/ImageUpload/handleImageUpload";
import { nbSelectStyles } from "../../../Common/FormShell/nbSelectStyles";
import { addTypeValidation } from "../../../Constant/constant";
import { getSingleType } from "../../../../redux/type/action";
import UnsavedChangesGuard from "../../Category/sections/UnsavedChangesGuard";
import { CATEGORY_STATUS } from "../../Category/constants";
import { usePermissions } from "../../../../Hooks/usePermissions";
import { MENU, PERMISSION } from "../../../../lib/permissions";
import axiosInstance from "../../../../redux/api";
import "../../../Common/FormShell/FormShell.css";

const ACCEPTED_TYPES = "image/png,image/jpeg,image/webp,image/svg+xml";
const MAX_SIZE_MB = 5;
const POSITION_MIN_TYPE = 1;
const POSITION_MAX_TYPE = 50;

const PREVIEW_TABS = [
  { key: "storefront", label: "Storefront", Icon: LayoutGrid },
  { key: "listing", label: "Admin list", Icon: ListIcon },
  { key: "search", label: "Search", Icon: Search },
];

const INITIAL_VALUES = {
  status: CATEGORY_STATUS.ACTIVE,
  name: "",
  ar_name: "",
  image: "",
  position: 1,
  home_view: false,
};

const slugify = (str = "") =>
  String(str)
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

const TypeForm = ({ mode = "create", embeddedId, onDone, onCancel }) => {
  const isEdit = mode === "edit";
  const { id: routeId } = useParams();
  const id = embeddedId ?? routeId;
  const { t } = useTranslation();
  const navigate = useNavigate();
  // Drawer mode: save/cancel resolve to callbacks instead of route changes.
  const embedded = typeof onDone === "function";
  const finishSave = () => (embedded ? onDone() : navigate("/type"));
  const finishCancel = () => (embedded ? onCancel?.() : navigate("/type"));
  const dispatch = useDispatch();
  const { hasAction } = usePermissions();

  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [previewTab, setPreviewTab] = useState("storefront");
  const fileInputRef = useRef(null);

  const [allSubcategoryOptions, setAllSubcategoryOptions] = useState([]);
  const [selectedSubcategories, setSelectedSubcategories] = useState([]);
  const [initialSubcategoryIds, setInitialSubcategoryIds] = useState([]);
  const [subcategoriesDirty, setSubcategoriesDirty] = useState(false);

  const singleType = useSelector(
    (state) => state.reusableSlice.singleType,
  ) || {};

  const isEditable = isEdit
    ? hasAction(MENU.CATALOGUE, PERMISSION.EDIT)
    : true;
  const readOnly = isEdit && !isEditable;

  // Fetch the sub-category list for the picker (always). In edit mode, one
  // extra call to the reverse-lookup endpoint tells us which sub-categories
  // already include this type — no per-sub-category fan-out.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const scRes = await axiosInstance.get("admin/sub-categories", {
          params: { page: 1, search_key: "", status: "", per_page: 500 },
        });
        if (cancelled) return;
        const subs = scRes.data?.response || [];
        const options = subs.map((s) => ({ value: s.id, label: s.title }));
        setAllSubcategoryOptions(options);

        if (isEdit && id) {
          const linkRes = await axiosInstance.get(
            `admin/types/${id}/sub-categories`,
          );
          if (cancelled) return;
          const linkedSubs = linkRes.data?.response ?? linkRes.data ?? [];
          const belongsTo = linkedSubs
            .map((s) => (typeof s === "number" ? s : s.id ?? s.sub_category_id))
            .filter(Boolean);
          setInitialSubcategoryIds(belongsTo);
          setSelectedSubcategories(
            options.filter((o) => belongsTo.includes(o.value)),
          );
        }
      } catch (err) {
        console.error("Failed to fetch sub-category data:", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isEdit, id]);

  // Fetch type record in edit mode
  useEffect(() => {
    if (isEdit && id) dispatch(getSingleType(id));
  }, [dispatch, id, isEdit]);

  // Guard against the Redux record lagging when switching targets in the
  // drawer: only seed once the loaded record matches the requested id.
  const recordMatches =
    !!singleType && Number(singleType.id) === Number(id);
  const initialValues = useMemo(() => {
    if (!isEdit || !recordMatches) return INITIAL_VALUES;
    return {
      status: singleType?.status || CATEGORY_STATUS.ACTIVE,
      name: singleType?.name || "",
      ar_name: singleType?.ar_name || "",
      image: singleType?.image || "",
      position: singleType?.position || 1,
      home_view: singleType?.home_view || false,
    };
  }, [isEdit, recordMatches, singleType]);

  // Read a sub-category's current type ids, then add or remove this type and
  // write the merged set back — preserving its other type links. Fetches only
  // the sub-categories that actually changed (not every one).
  const setTypeOnSubcategory = async (subId, typeId, include) => {
    const res = await axiosInstance.get("admin/types/sub-category-types", {
      params: { sub_category_id: subId },
    });
    const existing = (res.data?.response || []).map((tt) =>
      typeof tt === "number" ? tt : tt.type_id ?? tt.id,
    );
    const updated = include
      ? [...new Set([...existing, typeId])]
      : existing.filter((tid) => tid !== typeId);
    await axiosInstance.put("admin/types/sub-category-types", {
      sub_category_id: subId,
      type_ids: updated,
    });
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: addTypeValidation,
    onSubmit: async (values) => {
      if (readOnly) return;
      setSubmitting(true);
      try {
        const data = { ...values, image: values.image || "" };

        if (isEdit) {
          // Update the type itself
          const payload = { ...data, id: singleType?.id ?? Number(id) };
          const response = await axiosInstance.put("admin/types", payload);

          if (!response?.data?.status) {
            await swal({
              text: response?.data?.message || "Failed to update type",
              icon: "error",
              button: "Close",
              timer: 1500,
            });
            setSubmitting(false);
            return;
          }

          // Sync sub-category-type links: add to newly selected, remove from
          // deselected — preserving each sub-category's other type links.
          const typeId = Number(id);
          const newSubcategoryIds = selectedSubcategories.map((c) => c.value);
          const added = newSubcategoryIds.filter(
            (sid) => !initialSubcategoryIds.includes(sid),
          );
          const removed = initialSubcategoryIds.filter(
            (sid) => !newSubcategoryIds.includes(sid),
          );

          await Promise.all([
            ...added.map((subId) => setTypeOnSubcategory(subId, typeId, true)),
            ...removed.map((subId) =>
              setTypeOnSubcategory(subId, typeId, false),
            ),
          ]);

          await swal({
            text: "Type updated successfully",
            icon: "success",
            button: "Close",
            timer: 1500,
          });
          finishSave();
        } else {
          // CREATE
          const response = await axiosInstance.post("admin/types", data);
          if (response?.data?.status) {
            const newTypeId = response.data.response?.id;

            if (newTypeId && selectedSubcategories.length > 0) {
              await Promise.all(
                selectedSubcategories.map(async (sub) => {
                  try {
                    await setTypeOnSubcategory(sub.value, newTypeId, true);
                  } catch (err) {
                    console.error(
                      `Failed to link type to sub-category ${sub.label}:`,
                      err,
                    );
                  }
                }),
              );
            }

            await swal({
              text: "Type created successfully",
              icon: "success",
              button: "Close",
              timer: 1500,
            });
            finishSave();
          } else {
            await swal({
              text: response?.data?.message || "Failed to create type",
              icon: "error",
              button: "Close",
              timer: 1500,
            });
          }
        }
      } catch (err) {
        await swal({
          text: err?.response?.data?.message || "Something went wrong",
          icon: "error",
          button: "Close",
          timer: 1500,
        });
      } finally {
        setSubmitting(false);
      }
    },
  });

  const { values, errors, touched } = formik;
  const isDirty = (formik.dirty || subcategoriesDirty) && !submitting;
  const errorCount = Object.keys(errors).filter((k) => touched[k]).length;
  const imageUrl = values.image;
  const imageError = touched.image && errors.image;
  const isActive = Number(values.status) === CATEGORY_STATUS.ACTIVE;
  const visibleInHeader = Boolean(values.home_view);

  const previewName =
    (values.name || "").trim() ||
    (isEdit ? singleType?.name : "Type name") ||
    "Type name";
  const previewArName = (values.ar_name || "").trim();
  const previewSlug = slugify(values.name) || "type-slug";
  const firstParent = selectedSubcategories[0];
  const firstParentSlug = firstParent ? slugify(firstParent.label) : "sub-category";

  const processFile = async (file) => {
    if (!file || readOnly) return;
    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
      formik.setFieldError("image", `Image must be under ${MAX_SIZE_MB}MB`);
      formik.setFieldTouched("image", true, false);
      return;
    }
    setUploading(true);
    try {
      await handleImageUpload(
        file,
        { title: "images", name: "image" },
        "image",
        formik,
      );
    } catch {
      formik.setFieldError("image", "Image upload failed. Try again.");
      formik.setFieldTouched("image", true, false);
    } finally {
      setUploading(false);
    }
  };

  const onPickFile = (e) => {
    const file = e.target.files?.[0];
    processFile(file);
    e.target.value = "";
  };
  const onTileClick = () => {
    if (readOnly) return;
    fileInputRef.current?.click();
  };
  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (readOnly) return;
    processFile(e.dataTransfer.files?.[0]);
  };

  const onDiscard = () => {
    if (
      isDirty &&
      !window.confirm(
        t("You have unsaved changes. Leave this page and discard them?"),
      )
    ) {
      return;
    }
    finishCancel();
  };

  // Embedded (drawer) mode: Esc runs the same guarded discard as the back button.
  useEffect(() => {
    if (!embedded) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onDiscard();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (readOnly) return;
    const errs = await formik.validateForm();
    formik.setTouched(
      Object.keys(errs).reduce((acc, k) => ({ ...acc, [k]: true }), {}),
    );
    if (Object.keys(errs).length > 0) {
      const firstKey = Object.keys(errs)[0];
      const el = document.querySelector(`[name="${firstKey}"]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        if (typeof el.focus === "function") el.focus({ preventScroll: true });
      }
      return;
    }
    formik.handleSubmit(e);
  };

  return (
    <div className="nb-page">
      <UnsavedChangesGuard when={isDirty} />

      <div className="nb-topbar">
        {embedded ? (
          <button type="button" className="nb-topbar__back" onClick={onDiscard}>
            <ChevronLeft size={14} aria-hidden="true" />
            {t("Types")}
          </button>
        ) : (
          <Link to="/type" className="nb-topbar__back">
            <ChevronLeft size={14} aria-hidden="true" />
            {t("Types")}
          </Link>
        )}
        <ChevronRight size={12} className="nb-topbar__sep" aria-hidden="true" />
        <span className="nb-topbar__current">
          {isEdit ? singleType?.name || t("Edit type") : t("New type")}
        </span>
        {readOnly ? (
          <span
            className="nb-topbar__draft"
            style={{
              color: "var(--nb-muted)",
              background: "#f1f5f9",
              borderColor: "var(--nb-border-strong)",
            }}
          >
            <Lock size={11} aria-hidden="true" />
            {t("Read only")}
          </span>
        ) : (
          isDirty && (
            <span className="nb-topbar__draft">
              <span className="nb-topbar__dot" aria-hidden="true" />
              {isEdit ? t("Edited · unsaved") : t("Draft · unsaved")}
            </span>
          )
        )}
      </div>

      <form className="nb-shell" onSubmit={handleSubmit} noValidate>
        <div className="nb-form">
          <header className="nb-hero">
            <span className="nb-hero__eyebrow">
              <Sparkles size={12} aria-hidden="true" />
              {t("Catalog")}
            </span>
            <h1 className="nb-hero__title">
              {isEdit ? t("Edit type") : t("Create a new type")}
            </h1>
            <p className="nb-hero__sub">
              {t(
                "Types are filter chips on sub-category pages — Eyeliner, Mascara, EDP. Link them to one or more sub-categories so shoppers can drill in.",
              )}
            </p>
          </header>

          {errorCount > 0 && (
            <div className="nb-banner" role="alert">
              <AlertCircle size={16} aria-hidden="true" />
              <span>
                {errorCount === 1
                  ? t("1 field needs your attention.")
                  : t(`${errorCount} fields need your attention.`)}
              </span>
            </div>
          )}

          {/* 01 Identity */}
          <section className="nb-section">
            <div className="nb-section__head">
              <span className="nb-section__step">01</span>
              <div>
                <h2 className="nb-section__title">{t("Identity")}</h2>
                <p className="nb-section__hint">
                  {t("Bilingual type name shown as a filter chip on sub-category pages.")}
                </p>
              </div>
            </div>
            <div className="nb-section__body">
              <div className="nb-grid-2">
                <FormField
                  name="name"
                  label={t("Type name (English)")}
                  required
                  touched={touched.name}
                  error={errors.name}
                >
                  <input
                    id="name"
                    name="name"
                    type="text"
                    className="nb-input"
                    placeholder={t("e.g. Eyeliner")}
                    value={values.name}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    autoComplete="off"
                    maxLength={120}
                    disabled={readOnly}
                  />
                </FormField>
                <FormField
                  name="ar_name"
                  label={t("Type name (Arabic)")}
                  touched={touched.ar_name}
                  error={errors.ar_name}
                >
                  <input
                    id="ar_name"
                    name="ar_name"
                    type="text"
                    className="nb-input"
                    placeholder={t("الاسم بالعربية")}
                    dir="rtl"
                    lang="ar"
                    value={values.ar_name}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    autoComplete="off"
                    maxLength={120}
                    disabled={readOnly}
                  />
                </FormField>
              </div>
            </div>
          </section>

          {/* 02 Sub-categories */}
          <section className="nb-section">
            <div className="nb-section__head">
              <span className="nb-section__step">02</span>
              <div>
                <h2 className="nb-section__title">
                  <Tag size={14} aria-hidden="true" style={{ marginRight: 6, opacity: 0.7 }} />
                  {t("Linked sub-categories")}
                </h2>
                <p className="nb-section__hint">
                  {t(
                    "Pick the sub-categories where this type will appear as a filter.",
                  )}
                </p>
              </div>
            </div>
            <div className="nb-section__body">
              <FormField
                name="subcategories"
                label={t("Sub-categories")}
                hint={t(
                  "Each selected sub-category will list this type in its allowed-types filter.",
                )}
              >
                <ReactSelect
                  inputId="subcategories"
                  isMulti
                  value={selectedSubcategories}
                  options={allSubcategoryOptions}
                  styles={nbSelectStyles}
                  placeholder={t("Select sub-categories…")}
                  isDisabled={readOnly}
                  onChange={(selected) => {
                    setSelectedSubcategories(selected || []);
                    setSubcategoriesDirty(true);
                  }}
                  menuPortalTarget={document.body}
                />
              </FormField>
            </div>
          </section>

          {/* 03 Image */}
          <section className="nb-section">
            <div className="nb-section__head">
              <span className="nb-section__step">03</span>
              <div>
                <h2 className="nb-section__title">{t("Icon image")}</h2>
                <p className="nb-section__hint">
                  {t(
                    "Optional — small icon shown next to the type chip. Square SVG or PNG works best.",
                  )}
                </p>
              </div>
            </div>
            <div className="nb-section__body">
              <div
                className={`nb-drop ${dragOver ? "is-drag" : ""} ${
                  imageError ? "is-error" : ""
                } ${imageUrl ? "has-image" : ""}`}
                onClick={onTileClick}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onTileClick();
                  }
                }}
                onDrop={onDrop}
                onDragOver={(e) => {
                  e.preventDefault();
                  if (!readOnly) setDragOver(true);
                }}
                onDragLeave={() => setDragOver(false)}
                role="button"
                tabIndex={readOnly ? -1 : 0}
                aria-label={t("Upload type icon")}
                aria-disabled={readOnly}
              >
                {imageUrl ? (
                  <>
                    <img src={imageUrl} alt={t("Type icon")} className="nb-drop__img" />
                    {!readOnly && (
                      <div className="nb-drop__overlay">
                        <Upload size={18} aria-hidden="true" />
                        <span>{t("Replace image")}</span>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="nb-drop__empty">
                    <span className="nb-drop__bubble">
                      {uploading ? <Spinner /> : <ImageIcon size={20} aria-hidden="true" />}
                    </span>
                    <div className="nb-drop__title">
                      {uploading ? t("Uploading…") : t("Drop an icon or click to upload")}
                    </div>
                    <div className="nb-drop__hint">
                      {t("PNG, JPG, WEBP or SVG · up to 5MB")}
                    </div>
                  </div>
                )}
                <input
                  ref={fileInputRef}
                  id="image"
                  name="image"
                  type="file"
                  accept={ACCEPTED_TYPES}
                  onChange={onPickFile}
                  hidden
                  disabled={uploading || readOnly}
                />
              </div>
              {imageUrl && !readOnly && (
                <div className="nb-thumbrow">
                  <button type="button" className="nb-btn nb-btn--ghost nb-btn--sm" onClick={onTileClick}>
                    <Upload size={13} aria-hidden="true" />
                    {t("Replace")}
                  </button>
                  <button
                    type="button"
                    className="nb-btn nb-btn--danger-ghost nb-btn--sm"
                    onClick={() => formik.setFieldValue("image", "")}
                  >
                    <Trash2 size={13} aria-hidden="true" />
                    {t("Remove")}
                  </button>
                </div>
              )}
              {imageError && (
                <p className="nb-fielderror" role="alert">
                  {t(errors.image)}
                </p>
              )}
            </div>
          </section>

          {/* 04 Visibility */}
          <section className="nb-section">
            <div className="nb-section__head">
              <span className="nb-section__step">04</span>
              <div>
                <h2 className="nb-section__title">{t("Visibility")}</h2>
                <p className="nb-section__hint">
                  {t("Status, ordering, and whether shoppers see it on the home page.")}
                </p>
              </div>
            </div>
            <div className="nb-section__body">
              <div className="nb-grid-2">
                <FormField
                  name="status"
                  label={t("Status")}
                  touched={touched.status}
                  error={errors.status}
                >
                  <div className="nb-toggle" role="radiogroup" aria-label={t("Status")}>
                    <button
                      type="button"
                      role="radio"
                      aria-checked={isActive}
                      className={`nb-toggle__opt ${isActive ? "is-on" : ""}`}
                      onClick={() => formik.setFieldValue("status", CATEGORY_STATUS.ACTIVE)}
                      disabled={readOnly}
                    >
                      <span className="nb-toggle__dot nb-toggle__dot--on" />
                      {t("Active")}
                    </button>
                    <button
                      type="button"
                      role="radio"
                      aria-checked={!isActive}
                      className={`nb-toggle__opt ${!isActive ? "is-off" : ""}`}
                      onClick={() => formik.setFieldValue("status", CATEGORY_STATUS.INACTIVE)}
                      disabled={readOnly}
                    >
                      <span className="nb-toggle__dot nb-toggle__dot--off" />
                      {t("Inactive")}
                    </button>
                  </div>
                </FormField>

                <FormField
                  name="position"
                  label={t("Position")}
                  hint={t("Lower numbers appear first in the type filter.")}
                  touched={touched.position}
                  error={errors.position}
                >
                  <div className="nb-stepper">
                    <button
                      type="button"
                      className="nb-stepper__btn"
                      disabled={readOnly}
                      onClick={() =>
                        formik.setFieldValue(
                          "position",
                          Math.max(POSITION_MIN_TYPE, Number(values.position || 1) - 1),
                        )
                      }
                      aria-label={t("Decrease")}
                    >
                      −
                    </button>
                    <input
                      id="position"
                      name="position"
                      type="number"
                      inputMode="numeric"
                      min={POSITION_MIN_TYPE}
                      max={POSITION_MAX_TYPE}
                      className="nb-input nb-input--center"
                      value={values.position}
                      onChange={(e) => {
                        const v = e.target.value;
                        formik.setFieldValue("position", v === "" ? "" : Number(v));
                      }}
                      onBlur={formik.handleBlur}
                      disabled={readOnly}
                    />
                    <button
                      type="button"
                      className="nb-stepper__btn"
                      disabled={readOnly}
                      onClick={() =>
                        formik.setFieldValue(
                          "position",
                          Math.min(POSITION_MAX_TYPE, Number(values.position || 0) + 1),
                        )
                      }
                      aria-label={t("Increase")}
                    >
                      +
                    </button>
                  </div>
                </FormField>
              </div>

              <label className="nb-switchrow">
                <input
                  id="home_view"
                  name="home_view"
                  type="checkbox"
                  checked={visibleInHeader}
                  onChange={(e) =>
                    formik.setFieldValue("home_view", e.target.checked)
                  }
                  disabled={readOnly}
                />
                <span className="nb-switchrow__track" aria-hidden="true">
                  <span className="nb-switchrow__thumb" />
                </span>
                <span className="nb-switchrow__text">
                  <strong>{t("Show on home page")}</strong>
                  <span>
                    {t("Surface this type on the storefront home page as a quick filter shortcut.")}
                  </span>
                </span>
              </label>
            </div>
          </section>
        </div>

        <aside className="nb-preview">
          <div className="nb-preview__sticky">
            <div className="nb-preview__head">
              <span className="nb-preview__eyebrow">
                <span className="nb-preview__pulse" aria-hidden="true" />
                {t("Live preview")}
              </span>
              <div className="nb-preview__tabs" role="tablist">
                {PREVIEW_TABS.map(({ key, label, Icon }) => (
                  <button
                    key={key}
                    type="button"
                    role="tab"
                    aria-selected={previewTab === key}
                    className={`nb-preview__tab ${previewTab === key ? "is-active" : ""}`}
                    onClick={() => setPreviewTab(key)}
                  >
                    <Icon size={13} aria-hidden="true" />
                    {t(label)}
                  </button>
                ))}
              </div>
            </div>

            <div className="nb-preview__stage">
              {previewTab === "storefront" && (
                <div className="nb-stage nb-stage--storefront">
                  <div className="nb-store-frame">
                    <div className="nb-store-frame__chrome">
                      <span /> <span /> <span />
                      <em>nysmah.com / {firstParentSlug}</em>
                    </div>
                    <div className="nb-store-card">
                      <span className="nb-store-card__parent">
                        {firstParent
                          ? `${firstParent.label} › ${t("Filter by type")}`
                          : t("Pick a sub-category →")}
                      </span>
                      <div className="nb-tag-row" style={{ marginTop: 6 }}>
                        <span
                          className="nb-tag"
                          style={{
                            background: "var(--nb-primary)",
                            borderColor: "var(--nb-primary)",
                            color: "#fff",
                            height: 28,
                            padding: "0 12px",
                            fontWeight: 600,
                          }}
                        >
                          {imageUrl && (
                            <img
                              src={imageUrl}
                              alt=""
                              style={{
                                width: 14,
                                height: 14,
                                objectFit: "contain",
                                filter: "brightness(0) invert(1)",
                              }}
                            />
                          )}
                          {previewName}
                        </span>
                        <span className="nb-tag nb-tag--muted">{t("All")}</span>
                        <span className="nb-tag nb-tag--muted">{t("Other type")}</span>
                        <span className="nb-tag nb-tag--muted">{t("Other type")}</span>
                      </div>
                      {previewArName && (
                        <p className="nb-store-card__sub" style={{ marginTop: 4 }}>
                          {previewArName}
                        </p>
                      )}
                      {selectedSubcategories.length === 0 && (
                        <p
                          style={{
                            margin: "8px 0 0",
                            fontSize: 11.5,
                            color: "var(--nb-subtle)",
                            fontStyle: "italic",
                          }}
                        >
                          {t(
                            "Link this type to at least one sub-category so it shows up here.",
                          )}
                        </p>
                      )}
                      {!isActive && (
                        <span className="nb-store-card__hidden">
                          {t("Hidden — won't appear on storefront")}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {previewTab === "listing" && (
                <div className="nb-stage nb-stage--listing">
                  <div className="nb-listing-frame">
                    <div className="nb-listing-head">
                      <span>{t("Type")}</span>
                      <span>{t("Status")}</span>
                      <span>{t("Sub-categories")}</span>
                    </div>
                    <div className="nb-listing-row">
                      <div className="nb-listing-row__brand">
                        <div className={`nb-listing-avatar ${imageUrl ? "" : "is-empty"}`}>
                          {imageUrl ? (
                            <img src={imageUrl} alt={previewName} />
                          ) : (
                            <span>{previewName.charAt(0).toUpperCase()}</span>
                          )}
                        </div>
                        <div className="nb-listing-row__info">
                          <strong>{previewName}</strong>
                          <span className="nb-listing-row__pos">
                            #{values.position || POSITION_MIN_TYPE}
                          </span>
                        </div>
                      </div>
                      <div className="nb-listing-row__col">
                        <span
                          className={`nb-badge ${
                            isActive ? "nb-badge--on" : "nb-badge--off"
                          }`}
                        >
                          <span className="nb-badge__dot" aria-hidden="true" />
                          {isActive ? t("Active") : t("Inactive")}
                        </span>
                      </div>
                      <div className="nb-listing-row__col nb-listing-row__col--muted">
                        {selectedSubcategories.length === 0 ? (
                          <span className="nb-tag nb-tag--empty">{t("none")}</span>
                        ) : (
                          <div className="nb-tag-row">
                            {selectedSubcategories.slice(0, 2).map((c) => (
                              <span key={c.value} className="nb-tag nb-tag--muted">
                                {c.label}
                              </span>
                            ))}
                            {selectedSubcategories.length > 2 && (
                              <span className="nb-tag nb-tag--muted">
                                +{selectedSubcategories.length - 2}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {previewTab === "search" && (
                <div className="nb-stage nb-stage--search">
                  <div className="nb-search-frame">
                    <div className="nb-search-head">
                      <Search size={12} aria-hidden="true" />
                      <span>nysmah</span>
                    </div>
                    <div className="nb-search-result">
                      <div className="nb-search-result__url">
                        <span className="nb-search-result__favicon">
                          {imageUrl ? (
                            <img src={imageUrl} alt="" />
                          ) : (
                            <span>{previewName.charAt(0).toUpperCase()}</span>
                          )}
                        </span>
                        <div>
                          <span className="nb-search-result__site">nysmah.com</span>
                          <span className="nb-search-result__path">
                            › {firstParentSlug} › ?type={previewSlug}
                          </span>
                        </div>
                      </div>
                      <h4 className="nb-search-result__title">
                        {previewName} — Nysmah
                      </h4>
                      <p className="nb-search-result__desc">
                        {selectedSubcategories.length === 0
                          ? t(
                              "Link this type to a sub-category to give it a discoverable filter URL.",
                            )
                          : t(
                              `Filter ${previewName} products inside ${selectedSubcategories
                                .slice(0, 3)
                                .map((c) => c.label)
                                .join(", ")}${
                                selectedSubcategories.length > 3
                                  ? ` and ${selectedSubcategories.length - 3} more`
                                  : ""
                              }.`,
                            )}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="nb-preview__caption">
              {previewTab === "storefront" &&
                t("Filter chip shown on linked sub-category pages.")}
              {previewTab === "listing" && t("Row shown in the admin Type List.")}
              {previewTab === "search" &&
                t("Filter URL on Google — depends on linked sub-categories.")}
            </div>
          </div>
        </aside>

        {!readOnly && (
          <div className={`nb-actionbar ${isDirty || submitting ? "is-visible" : ""}`}>
            <div className="nb-actionbar__inner">
              <span className="nb-actionbar__status">
                {submitting ? (
                  <>
                    <span className="nb-actionbar__spin" />
                    {isEdit ? t("Saving changes…") : t("Creating type…")}
                  </>
                ) : errorCount > 0 ? (
                  <>
                    <AlertCircle size={14} aria-hidden="true" />
                    {errorCount === 1
                      ? t("1 error to fix before saving")
                      : t(`${errorCount} errors to fix before saving`)}
                  </>
                ) : (
                  <>
                    <span className="nb-actionbar__dot" aria-hidden="true" />
                    {t("Unsaved changes")}
                  </>
                )}
              </span>
              <div className="nb-actionbar__btns">
                <button
                  type="button"
                  className="nb-btn nb-btn--ghost"
                  onClick={onDiscard}
                  disabled={submitting}
                >
                  {t("Discard")}
                </button>
                <button
                  type="submit"
                  className="nb-btn nb-btn--primary"
                  disabled={submitting}
                >
                  {submitting ? (
                    <Spinner />
                  ) : isEdit ? (
                    t("Save changes")
                  ) : (
                    t("Create type")
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </form>
    </div>
  );
};

export default TypeForm;
